/*
 * Description:
 * ---------------------
 * The Firewall screen's Rules: the operator's own rules, outbound and
 * inbound, as AWS shows a security group's (FirewallRuleTable), one direction
 * edited and saved at a time; the public lists of known-bad addresses with
 * when each was last fetched; the countries blocked; and the flood limits,
 * which are always on. Every change is a firewall change, applied and
 * confirmed by the panel's apply, with every other setting sent as it is.
 * Wording in firewallWords.js.
 *
 * ! file components/FirewallRules.js
 */
import React, { useState } from "react";
import { FiX } from "react-icons/fi";
import { TbWorldOff } from "react-icons/tb";
import { useConfirm } from "./ConfirmModal";
import { RulesEditor, RulesView } from "./FirewallRuleTable";
import * as W from "./firewallWords";

const DIRECTIONS = ["out", "in"];

export default function FirewallRules({ view, groups, locked, apply, settings }) {
  const confirm = useConfirm();
  const [direction, setDirection] = useState("out");
  const [editing, setEditing] = useState(false);
  const [country, setCountry] = useState("");
  const rules = view.rules || [];
  const countries = view.block_countries || [];
  const lists = view.lists || [];
  const of = (dir) => rules.filter((r) => r.direction === dir);
  /* One direction's rules saved together; the other direction's go back as
     they are. */
  const save = async (edited, everything) => {
    if (everything && !(await confirm(W.RULES.everythingAsk))) return;
    const others = rules.filter((r) => r.direction !== direction);
    if (await apply({ ...settings, rules: direction === "out" ? [...edited, ...others] : [...others, ...edited] })) setEditing(false);
  };
  const addCountry = (code) => {
    setCountry("");
    if (code && !countries.includes(code)) apply({ ...settings, block_countries: [...countries, code] });
  };
  const offered = W.COUNTRY_CODES.filter((c) => !countries.includes(c))
    .map((c) => ({ code: c, name: W.countryName(c) }))
    .sort((a, b) => a.name.localeCompare(b.name));
  return (
    <div className="fw-rules">
      {!view.on && <div className="fw-notice is-warn" role="note"><span>{W.RULES.needsOn}</span></div>}
      <section className="fw-card fw-sg">
        <nav className="fw-sg-tabs" role="tablist" aria-label={W.RULES.title}>
          {DIRECTIONS.map((dir) => (
            <button key={dir} type="button" role="tab" aria-selected={dir === direction} className={dir === direction ? "is-on" : undefined}
              disabled={editing && dir !== direction} onClick={() => setDirection(dir)}>
              {W.RULE_TABLE.tabs[dir]} <em>{of(dir).length}</em>
            </button>
          ))}
        </nav>
        {!Array.isArray(view.rules) ? (
          <p className="fw-muted">{W.RULES.older}</p>
        ) : editing ? (
          <RulesEditor key={direction} direction={direction} rules={of(direction)} groups={groups} boxTime={view.box_time}
            locked={locked} onCancel={() => setEditing(false)} onSave={save} />
        ) : (
          <RulesView direction={direction} rules={of(direction)} groups={groups} stopped={view.dropped?.rules || {}}
            locked={locked} onEdit={() => setEditing(true)} />
        )}
      </section>

      <section className="fw-card">
        <header className="fw-card-head is-split">
          <div>
            <h3 className="fw-card-title">{W.LISTS.title}</h3>
            <p>{W.LISTS.means}</p>
          </div>
          <button type="button" role="switch" aria-checked={!!view.blocklists} aria-label={W.LISTS.on} className="fw-switch is-small"
            disabled={locked} onClick={() => apply({ ...settings, blocklists: !view.blocklists })}>
            <span className="fw-switch-word">{view.blocklists ? "On" : "Off"}</span>
            <span className="fw-track" aria-hidden="true"><span className="fw-knob" /></span>
          </button>
        </header>
        {lists.length > 0 && (
          <ul className="fw-lists">
            {lists.map((l) => (
              <li key={l.name}>
                <strong>{l.name}</strong>
                <span>{l.about}</span>
                <small className={l.error ? "is-bad" : undefined}>{l.error ? l.error : `${W.LISTS.entries(l.entries)}, ${W.LISTS.updated(l.updated)}`}</small>
              </li>
            ))}
          </ul>
        )}
        <p className="fw-muted">{W.plural(view.dropped?.threat || 0, "try", "tries")} stopped.</p>
      </section>

      <section className="fw-card">
        <header className="fw-card-head">
          <h3 className="fw-card-title"><TbWorldOff size={18} aria-hidden="true" /> {W.COUNTRIES.title}</h3>
          <p>{W.COUNTRIES.means}</p>
        </header>
        {view.countries_pending && <p className="fw-inline-note is-warn">{W.COUNTRIES.pending}</p>}
        {countries.length === 0 && <p className="fw-muted">{W.COUNTRIES.none}</p>}
        {countries.length > 0 && (
          <ul className="fw-tags">
            {countries.map((c) => (
              <li key={c}>
                <span>{W.countryName(c)}</span>
                <button type="button" aria-label={`Unblock ${W.countryName(c)}`} disabled={locked}
                  onClick={() => apply({ ...settings, block_countries: countries.filter((x) => x !== c) })}><FiX size={13} /></button>
              </li>
            ))}
          </ul>
        )}
        <select className="fw-input" value={country} disabled={locked} aria-label={W.COUNTRIES.add}
          onChange={(e) => addCountry(e.target.value)}>
          <option value="">{W.COUNTRIES.add}</option>
          {offered.map((c) => <option key={c.code} value={c.code}>{c.name}</option>)}
        </select>
        {countries.length > 0 && <p className="fw-muted">{W.plural(view.dropped?.country || 0, "try", "tries")} stopped.</p>}
      </section>

      <section className="fw-card">
        <h3 className="fw-card-title">{W.FLOOD.title}</h3>
        <p className="fw-muted">{W.FLOOD.text} {W.plural(view.dropped?.flood || 0, "try", "tries")} stopped.</p>
      </section>
    </div>
  );
}
